import React, { useEffect, useState } from 'react';
import { Box, Button, CircularProgress, Dialog, IconButton, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import LocationPicker from './LocationPicker';
import JobService from '../../services/JobService';
import { errorMessage } from '../../services/api';
import { useToast } from '../../contexts/ToastContext';

/** Full-screen map to set or move a job's location (tap/drag the pin, my position, or typed coordinates). */
export default function LocationDialog({ open, job, onClose, onSaved }) {
    const { t } = useTranslation();
    const { showToast } = useToast();
    const [value, setValue] = useState(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (open) setValue(job.latitude != null ? { lat: job.latitude, lng: job.longitude } : null);
    }, [open, job]);

    const save = async () => {
        setSaving(true);
        try {
            const updated = await JobService.setLocation(job.id, value ? value.lat : null, value ? value.lng : null);
            showToast(t('location.saved'), 'success');
            onSaved(updated);
        } catch (err) {
            showToast(errorMessage(err, t('common.error')), 'error');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onClose={saving ? undefined : onClose} fullScreen>
            <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1.25, pt: 'calc(10px + env(safe-area-inset-top, 0px))', borderBottom: '1px solid #E2E8F0' }}>
                    <IconButton onClick={onClose} disabled={saving} aria-label={t('common.close')}><CloseIcon /></IconButton>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography noWrap sx={{ fontWeight: 700, fontSize: 16, color: '#1a2332' }}>{t('location.title')} · {job.title}</Typography>
                        <Typography noWrap sx={{ fontSize: 12.5, color: '#64748B' }}>{t('location.mapHint')}</Typography>
                    </Box>
                    <Button variant="contained" disableElevation onClick={save} disabled={saving} sx={{ minWidth: 100 }}>
                        {saving ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : t('common.save')}
                    </Button>
                </Box>
                <Box sx={{ flex: 1, minHeight: 0, p: { xs: 1, sm: 2 }, pb: 'calc(12px + env(safe-area-inset-bottom, 0px))' }}>
                    {open && <LocationPicker value={value} onChange={setValue} fill />}
                </Box>
            </Box>
        </Dialog>
    );
}
